/**
 * Chat Command
 * Interactive chat with Jai1 LLM Proxy
 * Default: Web browser mode | Alternative: Terminal TUI mode
 */
import { Command } from 'commander';
import React from 'react';
import { render } from 'ink';
import { ConfigService } from '../services/config.service.js';
import { LlmProxyService } from '../services/llm-proxy.service.js';
import { ChatApp } from '../ui/llm/LlmApp.js';
import { ValidationError } from '../errors/index.js';
import { createWebChatServer } from '../server/web-chat-server.js';

interface ChatCommandOptions {
    model?: string;
    terminal?: boolean;
    port?: string;
    noOpen?: boolean;
}

/**
 * Handle terminal chat command (TUI mode)
 */
async function handleTerminalChat(options: ChatCommandOptions): Promise<void> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError('Not initialized. Run "jai1 auth" first.');
    }

    const service = new LlmProxyService(config);

    // Render fullscreen app
    const { waitUntilExit, clear } = render(
        React.createElement(ChatApp, {
            service,
            initialModel: options.model,
        }),
        {
            // Enable fullscreen mode - clears terminal on exit
            exitOnCtrlC: false, // We handle exit via Esc
        }
    );

    await waitUntilExit();

    // Clear screen on exit for clean terminal
    clear();
}

/**
 * Handle web chat command (browser mode) - DEFAULT
 */
async function handleWebChat(options: ChatCommandOptions): Promise<void> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError('Not initialized. Run "jai1 auth" first.');
    }

    const port = options.port ? parseInt(options.port, 10) : undefined;

    // Create and start web server
    const server = createWebChatServer({
        config,
        port,
    });

    try {
        const { port: actualPort, sessionToken } = await server.start();
        const url = server.getUrl();

        // Open browser unless --no-open is specified
        if (!options.noOpen) {
            const { default: open } = await import('open');
            await open(url);
            console.log('🌐 Browser opened with chat interface');
        } else {
            console.log(`\n📋 Open this URL in your browser:\n   ${url}\n`);
        }

        console.log('Press Ctrl+C to stop the server\n');

        // Keep server running until Ctrl+C
        await new Promise<void>((resolve) => {
            process.on('SIGINT', async () => {
                console.log('\n\n🛑 Shutting down...');
                await server.stop();
                resolve();
            });

            process.on('SIGTERM', async () => {
                await server.stop();
                resolve();
            });
        });
    } catch (error) {
        if (error instanceof Error && error.message.includes('Port')) {
            throw new ValidationError(error.message + ' Try specifying a different port with --port.');
        }
        throw error;
    }
}

/**
 * Handle chat command
 * Default: Web mode | --terminal: Terminal TUI mode
 */
async function handleChatCommand(options: ChatCommandOptions): Promise<void> {
    if (options.terminal) {
        await handleTerminalChat(options);
    } else {
        await handleWebChat(options);
    }
}

/**
 * Create chat command
 */
export function createChatCommand(): Command {
    const cmd = new Command('chat')
        .description('AI chat with Jai1 LLM Proxy (default: web browser)')
        .option('--model <model>', 'Initial model to use (e.g., gpt-4o, claude-3-opus)')
        .option('-t, --terminal', 'Use terminal TUI mode instead of web browser')
        .option('--port <port>', 'Port for web server (default: auto-find available port)')
        .option('--no-open', "Don't auto-open browser (web mode only)")
        .action(async (options: ChatCommandOptions) => {
            await handleChatCommand(options);
        });

    return cmd;
}
